import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedLines } from "@/components/MaskedLines";
import { waLink } from "@/constants/content";
import { ImageDisclaimer } from "@/components/ImageDisclaimer";
import { scrollToSection } from "@/hooks/useLenis";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.82, 0.92]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);

  const enquire = () => scrollToSection("#enquire");
  const visit = () => scrollToSection("#talk-to-sales");

  return (
    <section id="hero" ref={ref} className="cp-hero" data-testid="hero-section">
      <motion.div className="cp-hero-bg" style={{ y, scale }}>
        <img src="/brochure/hero-day-render.png" alt="Central Plaza SCO complex render, Sector 10A Kharkhoda" fetchPriority="high" />
      </motion.div>
      <motion.div className="cp-hero-overlay" style={{ opacity: overlay }} />
      <div className="cp-hero-grain" />

      <motion.div className="cp-hero-content" style={{ y: contentY }}>
        <div className="cp-hero-top">
          <span className="cp-eyebrow gold" data-testid="hero-eyebrow">Buildaxis Developers Presents</span>
        </div>

        <MaskedLines
          className="cp-hero-title"
          baseDelay={0.15}
          lines={[<span key="1">CENTRAL</span>, <span key="2">PLAZA</span>]}
        />

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.8 }}
          className="cp-hero-sub"
          data-testid="hero-subtitle"
        >
          <p>Premium SCO (Shop Cum Office) Complex</p>
          <span className="cp-hero-divider" />
          <p>Sector 10A, Kharkhoda, Sonipat</p>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="cp-hero-tag"
          data-testid="hero-tagline"
        >
          The First Premium SCO Complex in Kharkhoda
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="cp-hero-ctas"
        >
          <button className="btn btn-gold" onClick={visit} data-testid="hero-book-visit-btn">
            Book Site Visit
          </button>
          <button className="btn btn-outline-light" onClick={enquire} data-testid="hero-enquire-btn">
            Enquire Now
          </button>
          <a
            href={waLink("Hi, I'd like to know more about Central Plaza / Central Heights at Sector 10A, Kharkhoda.")}
            target="_blank"
            rel="noreferrer"
            className="btn btn-ghost-light"
            data-testid="hero-whatsapp-btn"
          >
            WhatsApp
          </a>
        </motion.div>
        <ImageDisclaimer className="cp-hero-image-disclaimer" light />
      </motion.div>

      <motion.div
        className="cp-hero-scroll"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        data-testid="hero-scroll-indicator"
      >
        <ArrowDown size={16} />
        <span>Scroll</span>
      </motion.div>
    </section>
  );
};
